import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Breadcrumbs,
  Link,
  LinearProgress,
} from '@mui/material';
import TagIcon from '@mui/icons-material/Tag';
import ExtensionIcon from '@mui/icons-material/Extension';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import PublicIcon from '@mui/icons-material/Public';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BuildIcon from '@mui/icons-material/Build';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Footer from '../../../shared/components/Footer';
import { getAssetsByZone } from '../../assets/data/assetsMockData';
import { useZone } from '../../../shared/context/ZoneContext';

const assetImage = 'https://images.unsplash.com/photo-1581093588401-fbb62a02f120?w=600&h=500&fit=crop';

const detailFields = [
  { key: 'id', label: 'ID', icon: TagIcon },
  { key: 'model', label: 'Model', icon: ExtensionIcon },
  { key: 'statusField', label: 'Status', icon: WarningAmberIcon },
  { key: 'type', label: 'Type', icon: FilterAltIcon },
  { key: 'make', label: 'Make', icon: PrecisionManufacturingIcon },
  { key: 'detailLocation', label: 'Location', icon: PublicIcon },
];

const getStatusColor = (healthScore) => {
  if (healthScore < 50) return '#DC2626';
  if (healthScore < 70) return '#DC2626';
  if (healthScore < 80) return '#D97706';
  return '#16A34A';
};

const getStatusLabel = (healthScore) => {
  if (healthScore < 50) return 'Critical';
  if (healthScore < 70) return 'Critical';
  if (healthScore < 80) return 'Warning';
  return 'Good';
};

const TechnicianAssetDetailPage = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { selectedZone } = useZone();
  const assets = getAssetsByZone(selectedZone);
  const asset = assets.find((a) => String(a.id) === String(assetId));

  if (!asset) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5">Asset not found</Typography>
        <Button onClick={() => navigate('/technician_dashboard')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  const statusColor = getStatusColor(asset.healthScore);
  const statusLabel = getStatusLabel(asset.healthScore);
  const isEmergency = asset.recommendedAction.severity === 'Emergency Maintenance';

  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.375rem' }}>
            {asset.fullName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<ShowChartIcon />}
              sx={{
                borderColor: '#E5E7EB',
                color: 'text.primary',
                fontWeight: 600,
                px: 2.5,
                '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' },
              }}
            >
              Run Health Check
            </Button>
            <Button
              variant="contained"
              startIcon={<BuildIcon />}
              sx={{
                bgcolor: '#22C55E',
                color: '#fff',
                fontWeight: 600,
                px: 2.5,
                '&:hover': { bgcolor: '#16A34A' },
              }}
            >
              Start Service
            </Button>
          </Box>
        </Box>

        {/* Breadcrumb */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
          <Link
            component="button"
            underline="hover"
            onClick={() => navigate('/technician_dashboard')}
            sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
          >
            Assets
          </Link>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.primary', fontWeight: 500 }}>
            {asset.fullName}
          </Typography>
        </Breadcrumbs>

        {/* Asset Info Section */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          {/* Image */}
          <Card sx={{ flex: '0 0 45%', overflow: 'hidden', borderRadius: 3 }}>
            <Box
              component="img"
              src={assetImage}
              alt={asset.fullName}
              sx={{
                width: '100%',
                height: 380,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Card>

          {/* Detail Fields */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0 }}>
            {detailFields.map((field, index) => {
              const Icon = field.icon;
              let value = asset[field.key];
              let valueColor = 'text.primary';

              if (field.key === 'statusField') {
                value = statusLabel;
                valueColor = statusColor;
              }

              return (
                <Box
                  key={field.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1.75,
                    px: 2,
                    borderBottom: index < detailFields.length - 1 ? '1px solid #F3F4F6' : 'none',
                  }}
                >
                  <Icon sx={{ fontSize: 22, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {field.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: valueColor }}>
                      {value}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Bottom Cards */}
        <Box sx={{ display: 'flex', gap: 2.5 }}>
          {/* Health Score */}
          <Card sx={{ flex: 1, borderRadius: 3 }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'text.primary' }}>
                  Health Score
                </Typography>
                <ShowChartIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '2rem', color: 'text.primary', mb: 1 }}>
                {asset.healthScore}/100
              </Typography>
              <LinearProgress
                variant="determinate"
                value={asset.healthScore}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    bgcolor: '#22C55E',
                  },
                }}
              />
            </CardContent>
          </Card>

          {/* Failure Probability */}
          <Card sx={{ flex: 1, borderRadius: 3, border: '1px solid #E0F2FE' }}>
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'text.primary' }}>
                  Failure Probability
                </Typography>
                <WarningAmberIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '2rem', color: 'text.primary' }}>
                {asset.failureProbability}%
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                {asset.failureTimeframe}
              </Typography>
            </CardContent>
          </Card>

          {/* Recommended Action */}
          <Card
            sx={{
              flex: 1,
              borderRadius: 3,
              bgcolor: isEmergency ? '#FEF2F2' : '#FFF7ED',
              border: `1px solid ${isEmergency ? '#FECACA' : '#FED7AA'}`,
            }}
          >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'text.primary' }}>
                  Recommended Action
                </Typography>
                <BuildIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.9375rem',
                  color: isEmergency ? '#DC2626' : '#D97706',
                  mb: 0.5,
                }}
              >
                {asset.recommendedAction.severity}
              </Typography>
              <Typography variant="body2" sx={{ color: isEmergency ? '#DC2626' : '#92400E', fontSize: '0.8125rem' }}>
                {asset.recommendedAction.description}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default TechnicianAssetDetailPage;
